import Bugsnag from '@bugsnag/js'
import { Box, FormControl, InputAdornment, RadioGroup, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxInput from 'components/custom/AdxInput'
import AdxRadioButton from 'components/custom/AdxRadioButton'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { postNumDays } from 'services/api/modules/payroll/payroll-config/generalApi'
import * as yup from 'yup'

const ReminderModal = ({ children, numberOfDaysData }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const handleOpen = () => {
    setModalOpen(true)
  }
  const handleClose = () => {
    setModalOpen(false)
  }

  let calendar = []
  for (let day = 1; day <= 31; day += 1) {
    calendar.push(day)
  }
  let minutes = []
  for (let x = 0; x < 60; x += 10) {
    let min = x.toLocaleString('default', { minimumIntegerDigits: 2 })
    minutes.push(min)
  }
  let hours = []
  for (let y = 1; y <= 12; y++) {
    let hr = y.toLocaleString('default', { minimumIntegerDigits: 2 })
    hours.push(hr)
  }

  // const toggleStyle = () => {
  //   return {
  //     '&.Mui-selected': {
  //       color: 'alternate.main',
  //       backgroundColor: 'alternate.light',
  //       border: 1,
  //       '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
  //         borderTop: 1,
  //       },
  //     },
  //     '&.MuiButtonBase-root': { py: 0 },
  //     '&:hover': {
  //       color: 'alternate.main',
  //       backgroundColor: 'gray-background.main',
  //     },
  //   }
  // }
  const validationSchema = yup.object().shape({
    working_days_amount: yup
      .number()
      .min(1, 'This field must contain valid number of days')
      .max(31, 'This field must contain valid number of days')
      .when('working_days_status', {
        is: 1,
        then: yup.number().nullable().required('This field is required'),
        otherwise: yup.number().nullable(),
      }),
  })
  const queryClient = useQueryClient()
  const { mutate: mutateNumberOfDays } = useMutation(postNumDays, {
    onSuccess: () => {
      queryClient.invalidateQueries('numberOfWorkingDays')
      setIsLoading(false)
      setModalOpen(false)
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
        setIsLoading(false)
      } else {
        Bugsnag.notify(error)
        setIsLoading(false)
      }
    },
  })
  const handleSubmit = async (values) => {
    const { company_id, working_days_status, working_days_amount, reminder } = values
    const { hours, shift, minutes, ...rest } = reminder
    let data
    if (shift === 'am') {
      if (hours === '12') {
        data = {
          company_id,
          working_days: {
            status: working_days_status,
            amount: working_days_amount,
          },
          reminder: {
            ...rest,
            hours: parseInt(hours) - 12,
            minutes: parseInt(minutes),
          },
        }
      } else {
        data = {
          company_id,
          working_days: {
            status: working_days_status,
            amount: working_days_amount,
          },
          reminder: {
            ...rest,
            hours: parseInt(hours),
            minutes: parseInt(minutes),
          },
        }
      }
    } else {
      if (hours !== '12') {
        data = {
          company_id,
          working_days: {
            status: working_days_status,
            amount: working_days_amount,
          },
          reminder: {
            ...rest,
            hours: parseInt(hours) + 12,
            minutes: parseInt(minutes),
          },
        }
      } else {
        data = {
          company_id,
          working_days: {
            status: working_days_status,
            amount: working_days_amount,
          },
          reminder: {
            ...rest,
            hours: parseInt(hours),
            minutes: parseInt(minutes),
          },
        }
      }
    }
    setIsLoading(true)
    mutateNumberOfDays(data)
  }
  return (
    <>
      <Box onClick={handleOpen}>{children}</Box>
      <AdxFormModal
        open={isModalOpen}
        onClose={handleClose}
        handleClose={handleClose}
        title="General"
        childPadding={false}
        sxModal={{ width: 500 }}
      >
        <Formik
          initialValues={{
            company_id: numberOfDaysData ? numberOfDaysData.id : null,
            working_days_status: numberOfDaysData ? numberOfDaysData.working_days_status : 2,
            working_days_amount: numberOfDaysData ? numberOfDaysData.working_days : '',
            reminder: {
              day: numberOfDaysData?.reminder_details
                ? numberOfDaysData?.reminder_details?.day
                : null,
              hours: numberOfDaysData?.reminder_details
                ? numberOfDaysData?.reminder_details?.hours > 12
                  ? (numberOfDaysData?.reminder_details?.hours - 12)?.toLocaleString('default', {
                      minimumIntegerDigits: 2,
                    })
                  : numberOfDaysData?.reminder_details?.hours !== 0
                  ? numberOfDaysData?.reminder_details?.hours?.toLocaleString('default', {
                      minimumIntegerDigits: 2,
                    })
                  : '12'
                : null,
              minutes: numberOfDaysData?.reminder_details
                ? numberOfDaysData?.reminder_details?.minutes?.toLocaleString('default', {
                    minimumIntegerDigits: 2,
                  })
                : null,
              shift: numberOfDaysData?.reminder_details?.hours >= 12 ? 'pm' : 'am',
            },
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ values, handleChange, setFieldValue, errors, touched, handleSubmit }) => {
            const handleChangeDate = (_, newValue) => {
              setFieldValue('reminder.day', newValue)
            }
            const handleChangeShift = (_, newValue) => {
              setFieldValue('reminder.shift', newValue)
            }
            const handleWorkingDays = (_, newValue) => {
              setFieldValue('working_days_status', parseInt(newValue))
            }
            const control = {
              value: values.reminder.shift,
              onChange: handleChangeShift,
              exclusive: true,
            }
            return (
              <Form>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'start',
                    alignItems: 'start',
                    px: 3,
                    pb: 2,
                  }}
                >
                  {/* <Box py={1.75} borderBottom="1px solid" sx={{ borderColor: 'gray-line.main' }}>
                      <Typography color="light-gray-text.main" fontWeight={500}>
                        Payroll reminder
                      </Typography>
                    </Box>
                    <Box width={1} my={2}>
                      <Typography color="light-gray-text.main">Date</Typography>
                      <Box
                        sx={{
                          p: 2,
                          my: 2,
                          bgcolor: 'gray-background.main',
                          borderRadius: '7.5px',
                        }}
                      >
                        <Typography color="light-gray-text.main" variant="body2">
                          If the selected date falls on a non-working days or public holidays,
                          you'll be reminded on the working day before...
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {calendar?.map((day, index) => {
                          return (
                            <Field
                              name="reminder.day"
                              as={ToggleButton}
                              key={index}
                              value={day}
                              onChange={handleChangeDate}
                              disabled={day === 29 || (day === 30) | (day === 31)}
                              sx={{
                                '&.Mui-selected': {
                                  color: 'alternate.main',
                                  backgroundColor: 'alternate.light',
                                },
                                '&.MuiButtonBase-root': {
                                  py: 0,
                                  border: 'none',
                                },
                                '&:hover': {
                                  color: 'alternate.main',
                                  backgroundColor: 'gray-background.main',
                                },
                                width: '55px',
                                height: '55px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '10px',
                                bgcolor: day === values.reminder.day && 'alternate.main',
                                color: day === values.reminder.day && 'white.main',
                              }}
                            >
                              <Typography fontWeight="bold">{day}</Typography>
                            </Field>
                          )
                        })}
                      </Box>
                    </Box>
                    <Box marginY={4}>
                      <Typography sx={{ mb: 2 }} color="light-gray-text.main">
                        Time
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <AdxDropdown
                          name="reminder.hours"
                          sx={{
                            width: '100px',
                            '& .MuiOutlinedInput-root': { height: '55px' },
                            '& .MuiSvgIcon-root': { display: 'none' },
                          }}
                          value={values.reminder.hours}
                          onChange={handleChange}
                          renderValue={(hour) => {
                            return (
                              <Typography variant="h2" fontWeight="bold">
                                {hour}
                              </Typography>
                            )
                          }}
                        >
                          {hours?.map((hour, index) => (
                            <AdxMenuItem
                              key={index}
                              value={hour}
                              selectedvalue={values.reminder.hours}
                            >
                              {hour}
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                        <Typography variant="h2" mx={2}>
                          :
                        </Typography>
                        <AdxDropdown
                          name="reminder.minutes"
                          sx={{
                            width: '100px',
                            '& .MuiSelect-select': {},
                            '& .MuiOutlinedInput-root': { height: '55px' },
                            '& .MuiSvgIcon-root': { display: 'none' },
                          }}
                          value={values.reminder.minutes}
                          onChange={handleChange}
                          renderValue={(min) => (
                            <Typography variant="h2" fontWeight="bold">
                              {min}
                            </Typography>
                          )}
                        >
                          {minutes?.map((minute, index) => (
                            <AdxMenuItem
                              key={index}
                              value={minute}
                              selectedvalue={values.reminder.minutes}
                            >
                              {minute}
                            </AdxMenuItem>
                          ))}
                        </AdxDropdown>
                        <ToggleButtonGroup
                          name="shift"
                          {...control}
                          orientation="vertical"
                          sx={{ mx: 2 }}
                        >
                          <ToggleButton value="am" key="am" sx={{ ...toggleStyle() }}>
                            AM
                          </ToggleButton>
                          <ToggleButton value="pm" key="pm" sx={{ ...toggleStyle() }}>
                            PM
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                    </Box> */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography color="light-gray-text.main" fontWeight={500}>
                      Working days
                    </Typography>
                    <Box>
                      <FormControl>
                        <Field
                          as={RadioGroup}
                          name={`working_days_status`}
                          value={values?.working_days_status}
                          onChange={handleWorkingDays}
                          sx={{ display: 'flex' }}
                        >
                          <AdxFormControlLabel
                            type="no-fill"
                            selected={values?.working_days_status === 2}
                            value={2}
                            control={<AdxRadioButton />}
                            label="Total number of days"
                          />
                          <AdxFormControlLabel
                            type="no-fill"
                            selected={values?.working_days_status === 1}
                            value={1}
                            control={<AdxRadioButton />}
                            label="Custom days"
                          />
                        </Field>
                      </FormControl>
                    </Box>
                  </Box>
                  {values?.working_days_status === 1 && (
                    <Box sx={{ display: 'flex', gap: 1, width: 1 }}>
                      <AdxInput
                        label="Total working days"
                        name={`working_days_amount`}
                        endAdornment={<InputAdornment position="end">days</InputAdornment>}
                        sxInput={{
                          color: 'light-gray-text.main',
                          backgroundColor: 'white.main',
                        }}
                        onChange={handleChange}
                        size="standard"
                        type="number"
                        value={values.working_days_amount}
                        sx={{ width: '80%' }}
                        errorMessage={
                          <ErrorMessage name="working_days_amount" component={AdxErrorMessage} />
                        }
                        isErrorShown={touched?.working_days_amount && errors?.working_days_amount}
                      />
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-end"
                        alignItems="flex-start"
                        sx={{ height: '70px', width: '20%' }}
                      >
                        <Typography color="light-gray-text.main" sx={{ pb: 1 }}>
                          per month
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'end' },
                    px: 3,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'gray-line.main',
                    gap: 1,
                  }}
                >
                  <AdxButton
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ width: { xs: 1, md: '100px' } }}
                  >
                    Cancel
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    onClick={handleSubmit}
                    sx={{ width: { xs: 1, md: '200px' } }}
                    isLoading={isLoading}
                  >
                    Save
                  </AdxButton>
                </Box>
              </Form>
            )
          }}
        </Formik>
      </AdxFormModal>
    </>
  )
}

export default ReminderModal
